/* eslint-disable max-lines-per-function */
import './header.scss';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TextField, InputAdornment, CircularProgress } from '@mui/material';
import { ReactComponent as Bell } from '../../Assets/images/svg/dar_bell.svg';
import { ReactComponent as MagnifyingGlass } from '../../Assets/images/svg/dar_magnify.svg';
import Arrow from '../../Assets/images/svg/dar_arrow4.svg';
import darpanLogo from '../../Assets/images/svg/darpan_logo.svg';
import Autocomplete from '@mui/material/Autocomplete';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import { fetchUserInfoFromZoho } from './fetchUserInform';
import ProfilePicPlaceHolder from '../../Assets/images/png/profil-pic-placeholder-large.png';
import { useNavigate, useLocation } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { makeStyles } from '@mui/styles';
import Notifications from '../../Components/Notifications/notifications';
import { useAuth } from '../../Hooks/useAuth';
import UseAxios from '../../Api/http/http';
import { serviceName } from '../../Api/resources/serviceConstants';
import { NOTIFICATON } from '../../Api/resources/endPoint';
import { MODULE_NAMES } from '../../Constants/modules';

const hasPermissionAction = (permissions, moduleName, action) => {
  const permissionCandidates = [
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ];

  return permissionCandidates.some((candidate) =>
    Array.isArray(permissions?.[candidate])
    && permissions[candidate].includes(action),
  );
};

const hasSectionAccess = (sectionName, permissions) => {
  const lowerName = sectionName.toLowerCase();
  if (lowerName.includes('cr approval')) {
    return permissions?.['project:change-request']?.length > 0;
  }
  if (lowerName.includes('project status change')) {
    return (
      permissions?.['delivery:project-overview:project-status']?.length > 0
    );
  }
  if (lowerName.includes('timeline date')) {
    return (
      permissions?.['delivery:project-overview:timeline-change-request']
        ?.length > 0
    );
  }
  if (lowerName.includes('resource loading')) {
    return (
      permissions?.['delivery:resource-loading-plan']?.length > 0
      || permissions?.['delivery:resource-loading-approval-history']?.length > 0
    );
  }
  if (lowerName.includes('shift allowance')) {
    return (
      permissions?.['resource:shift-allowance-approval-history']?.length > 0
      || permissions?.['resource:shift-allowance']?.length > 0
    );
  }
  if (lowerName.includes('staffing request')) {
    return permissions?.['delivery:staffing-request']?.length > 0;
  }
  return true;
};

const useNoOptionsStyles = (noEmployeeData) =>
  makeStyles({
    noOptions: {
      display: noEmployeeData ? 'block' : 'none',
    },
  });

const CustomHeader = ({ open, handleDrawerOpen }) => {
  const [isDesktopView, setIsDesktopView] = useState(true);
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [loadingEmployeeList, setLoadingEmployeeList] = useState(false);
  const [noEmployeeData, setNoEmployeeData] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [showNotifications, setShowNotifications] = useState(false);
  const { userProfile } = useAuth();
  const [hasPendingApprovals, setHasPendingApprovals] = useState(false);
  const hasSearchAccess = useMemo(() => {
    const permissions = userProfile?.permissions || {};
    return Boolean(
      userProfile?.superAdmin
      || userProfile?.isAdmin
      || hasPermissionAction(
        permissions,
        MODULE_NAMES.GENERAL_CONFIG_SEARCH,
        'read',
      ),
    );
  }, [userProfile]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const reqConfig = {
          url: `${NOTIFICATON}/approval-pending-stats?emp_id=${userProfile?.employeeId || ''}`,
          method: 'get',
          headers: { 'content-type': 'application/json' },
          service: serviceName.PROJECT_MS,
        };
        const response = await UseAxios(reqConfig);
        if (response?.data?.success) {
          const sections = response.data?.data?.sections || [];
          const filtered = sections.filter((sec) =>
            hasSectionAccess(sec.section, userProfile?.permissions),
          );
          const total = filtered.reduce(
            (acc, cur) => acc + Number(cur.open_request || 0),
            0,
          );
          setHasPendingApprovals(total > 0);
        }
      } catch (error) {
        console.error('Error fetching approval stats in header:', error);
      }
    };

    if (userProfile?.employeeId) {
      fetchStats();
    } else {
      setHasPendingApprovals(false);
    }
  }, [userProfile]);
  const useNoOptionStyles = useNoOptionsStyles(noEmployeeData);
  const noOptionStyles = useNoOptionStyles();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth > 768);
      setIsMobileView(window.innerWidth <= 768);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (hasSearched && location.pathname !== '/employeeProfile') {
      setInputValue('');
      setSelectedEmployee({});
      setHasSearched(false);
    }
  }, [location.pathname, hasSearched]);

  useEffect(() => {
    if (!hasSearchAccess) {
      setInputValue('');
      setSelectedEmployee({});
      setEmployeeSuggestions([]);
      setNoEmployeeData(false);
      setLoadingEmployeeList(false);
    }
  }, [hasSearchAccess]);

  const handleInputChange = useCallback((event, newInputValue) => {
    setNoEmployeeData(false);
    if (newInputValue.length >= 3) {
      setLoadingEmployeeList(true);
      setInputValue(newInputValue);
      debouncedFetchEmployeeInfo(newInputValue);
    } else {
      setLoadingEmployeeList(false);
      setEmployeeSuggestions([]);
      setNoEmployeeData(false);
      setInputValue(newInputValue);
    }
  }, []);

  const debouncedFetchEmployeeInfo = useMemo(
    () => debounce((searchTerm) => fetchEmployeeData(searchTerm), 3000),
    [],
  );

  const fetchEmployeeData = async (searchTerm) => {
    try {
      const employeeInfo = await fetchUserInfoFromZoho(searchTerm);
      setLoadingEmployeeList(false);
      setNoEmployeeData(employeeInfo.length === 0);
      setEmployeeSuggestions(employeeInfo);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setLoadingEmployeeList(false);
      setNoEmployeeData(true);
    }
  };

  const handleAutocompleteChange = (event, newValue) => {
    if (newValue) {
      setSelectedEmployee(null);
      setHasSearched(true);
      navigate('/employee-profile', { state: { emailId: newValue.emailId } });
    }
  };

  return (
    <div className="edge-case horizontal-nav-bar-desktop-web-parent">
      <div
        className="horizontal-nav-bar-desktop-web"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
        }}
      >

        <div
          className="left-header-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isDesktopView ? '48px' : '12px',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {isDesktopView && (
            <div className="header-logo-toggle-section">
              <button
                className="collapse-button-header"
                onClick={handleDrawerOpen}
              >
                <img
                  src={Arrow}
                  alt="Toggle Sidebar"
                  style={{
                    transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                />
              </button>
              <div className="header-logo-container" style={{ display: 'flex', alignItems: 'center' }}>
                <img src={darpanLogo} alt="Darpan Logo" style={{ width: '96px', height: '24px' }} />
              </div>
            </div>
          )}
          <div className="logo-placeholder-parent" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="search-bar-area">
              {hasSearchAccess && (!isMobileView || !open) && (
                <Autocomplete
                  id="header-search-bar"
                  sx={{ width: '100%' }}
                  classes={{ noOptions: noOptionStyles.noOptions }}
                  value={selectedEmployee}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={handleAutocompleteChange}
                  loading={loadingEmployeeList}
                  loadingText={
                    <div className="header-employee-loading">
                      <CircularProgress size={24} />
                    </div>
                  }
                  noOptionsText={'No matches found'}
                  options={employeeSuggestions}
                  isOptionEqualToValue={(option, value) => option.fullName === value.fullName}
                  getOptionLabel={(option) => option.fullName || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <img
                        src={option.photoSignedUrl || ProfilePicPlaceHolder}
                        alt="Profile"
                        className="profile-image"
                        onError={(errorEvent) => {
                          errorEvent.target.onerror = null; // Prevents infinite loop in case the placeholder image also fails to load
                          errorEvent.target.src = ProfilePicPlaceHolder;
                        }}
                      />
                      {`${option.fullName} . ${option.employeeId}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="search-bar"
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      autoFocus={false} // Prevent autofocus
                      InputProps={{
                        ...params.InputProps,
                        style: {},
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            style={{
                              marginTop: '0',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              marginRight: isDesktopView ? '-10px' : '0px',
                            }}
                          >
                            {!isDesktopView ? (
                              <IconButton
                                color="primary"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                sx={{
                                  padding: '4px',
                                  marginLeft: '2px',
                                  ...(open && { display: 'none' }),
                                }}
                              >
                                <MenuIcon />
                              </IconButton>
                            ) : (
                              <MagnifyingGlass style={{ width: '20px', height: '20px' }} />
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        '& fieldset': { borderColor: '#b2b9c0' },
                        '& .MuiInputBase-root': {
                          height: '36px',
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          borderRadius: '18px',
                          paddingLeft: isDesktopView ? '12px !important' : '4px !important',
                        },
                        '& .MuiInputBase-input': {
                          color: '#1b1d1f',
                          flex: '1 1 auto !important',
                          minWidth: '0px !important',
                          paddingLeft: '6px !important',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderColor: '#1b1d1f',
                        },
                      }}
                    />
                  )}
                />
              )}
              {!hasSearchAccess && isMobileView && !open && (
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                  sx={{
                    padding: '4px',
                    marginLeft: '2px',
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </div>
          </div>
        </div>

        <div className="user-actions-area">
          <div className="actions" style={{ position: 'relative' }}>
            <div className="bell-icon-container" style={{ position: 'relative' }}>
              <Bell
                className="bell-icon"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowNotifications((prev) => !prev)}
              />
              {hasPendingApprovals && (
                <span className="bell-badge-dot" />
              )}
              {showNotifications && (
                <div
                  className="notifications-dropdown"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Notifications onClose={() => setShowNotifications(false)} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CustomHeader.propTypes = {
  open: PropTypes.bool.isRequired,
  handleDrawerOpen: PropTypes.func.isRequired,
};

export default CustomHeader;
